import React, { JSX, useEffect, useMemo, useRef } from 'react';
import ReactDomServer from 'react-dom/server';
import { WarningIcon } from '@chakra-ui/icons';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { useHtmlTranslations, useTranslations } from 'contexts/LocalizationContext';

import { useScannedRelevosContext } from '../../../../contexts/ScannedRelevosContext';
import colors from '../../../../styles/colors';
import { ItemAssignmentScannersModes, ItemAssignmentSelectMode } from '../UserSelect/types';
import RelevosList from './RelevosList';
import SumUpConfirmButtons from './SumUpConfirmButtons';

const AssignSumUp = (): JSX.Element | null => {
  const translations = useTranslations();
  const htmlTranslations = useHtmlTranslations();
  const {
    selectItemMode: scanMode,
    applicationUser,
    quickContact,
    scannedRelevos,
    setScannedRelevos,
    restaurant,
  } = useScannedRelevosContext();
  const restaurantCurrency = useMemo(() => restaurant?.currency, [restaurant]);
  const isCurrencyValid = useMemo(
    () => restaurantCurrency === applicationUser?.accountInfo?.accountBalance.currency,
    [applicationUser, restaurantCurrency],
  );
  const showCurrencyWarning = useMemo(
    () => !!restaurantCurrency && !!applicationUser?.accountInfo && !isCurrencyValid,
    [applicationUser?.accountInfo, isCurrencyValid, restaurantCurrency],
  );

  const isClientSelected = useMemo(() => applicationUser || quickContact, [applicationUser, quickContact]);

  const scrollToBottomRef = useRef<null | HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (scrollToBottomRef.current) {
      scrollToBottomRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (scannedRelevos.length === 1) {
      scrollToBottom();
    }
  }, [scannedRelevos]);

  if (showCurrencyWarning) {
    return (
      <Flex bg="red.100" p={2}>
        <WarningIcon w={10} h={6} />
        <Text>{translations('assign_items_card_user_invalid_currency_error')}</Text>
      </Flex>
    );
  }

  if (scannedRelevos.length === 0) {
    if (isClientSelected && ItemAssignmentScannersModes.includes(scanMode)) {
      return (
        <VStack p={4}>
          <Box textAlign="left" pt={1} width={'100%'}>
            <Text fontSize="20px">
              {htmlTranslations('relevos_scan_summary_heading', {
                '{{dishes-count}}': ReactDomServer.renderToString(
                  <Text as="b" color={'orange.500'}>
                    {scannedRelevos.length}
                  </Text>,
                ),
              })}
            </Text>
          </Box>
          <Box textAlign="left" width={'100%'}>
            <Text fontSize="20px">{translations('assign_relevos_info')}</Text>
          </Box>
          <Box textAlign="left" width={'100%'}>
            <Text color="grey.200">{translations('assign_relevos_info_extended')}</Text>
          </Box>
        </VStack>
      );
    } else {
      return null;
    }
  }

  const removeRelevo = (relevo: string) => {
    setScannedRelevos((prevState) => prevState.filter((el) => el.uId !== relevo));
  };

  if (scannedRelevos.length >= 1 && scanMode !== ItemAssignmentSelectMode.SELECT_CATEGORY) {
    return (
      <Box ref={scrollToBottomRef} backgroundColor={colors.white}>
        <Flex direction="column" pl={4} textAlign="left" pt={1}>
          <Flex justifyContent="space-between" alignItems="baseline" flexWrap="wrap">
            {isClientSelected && (
              <Text fontSize="20px" mr={4}>
                {htmlTranslations('relevos_scan_summary_heading', {
                  '{{dishes-count}}': ReactDomServer.renderToString(
                    <Text as="b" color={colors.orange[500]}>
                      {scannedRelevos.length}
                    </Text>,
                  ),
                  '{{user-id}}': ReactDomServer.renderToString(
                    <Text as="b">{applicationUser?.userId?.toString() || quickContact?.quickContactName}</Text>,
                  ),
                })}
              </Text>
            )}
            {isClientSelected && (
              <Box>
                <SumUpConfirmButtons />
              </Box>
            )}
          </Flex>
          {isClientSelected && <RelevosList onRemoveItem={removeRelevo} />}
        </Flex>
      </Box>
    );
  }
  return <></>;
};

export default AssignSumUp;
